import { RefObject, useEffect, useMemo, useState } from 'react';
import { debounce } from 'underscore';

import useTrackResize from './use-track-resize';

type TUseTrackBoundingClientProps = {
  ref: RefObject<HTMLElement | null> | HTMLElement | null;
  debounceTime?: number;
  rerunCounter?: number;
  open?: boolean;
  trackOnWindowResize?: boolean;
  trackOnWindowScroll?: boolean;
};

const useTrackBoundingClient = ({
  trackOnWindowResize,
  trackOnWindowScroll,
  ref,
  debounceTime,
  rerunCounter,
  open,
}: TUseTrackBoundingClientProps) => {
  const [boundingClientRect, setBoundingClientRect] = useState<DOMRect | null>(null);

  const resized = useTrackResize({
    trackWindow: trackOnWindowResize,
    trackType: 'debounced_and_throttled',
    ref,
  });

  const rerun = useMemo(() => {
    if (!trackOnWindowResize && rerunCounter === undefined) {
      return 0;
    }

    return (resized.width || 0) + (resized.height || 0) + (rerunCounter || 0);
  }, [trackOnWindowResize, rerunCounter, resized]);

  useEffect(() => {
    if (!open) return;

    const el = ref instanceof HTMLElement ? ref : ref?.current;
    if (!el) return;

    const fastThrottled = () => {
      const boundingClientRect = el.getBoundingClientRect();
      setBoundingClientRect(boundingClientRect);
    };

    fastThrottled();

    const debounced = debounceTime ? debounce(fastThrottled, debounceTime) : fastThrottled;

    el.addEventListener('resize', debounced, { passive: true });
    el.addEventListener('scroll', debounced, { passive: true });
    if (trackOnWindowScroll) {
      window.addEventListener('scroll', debounced, { passive: true });
    }

    return () => {
      el.removeEventListener('resize', debounced);
      el.removeEventListener('scroll', debounced);
      window.removeEventListener('scroll', debounced);
    };
  }, [ref, debounceTime, open, rerun, trackOnWindowScroll]);

  return boundingClientRect;
};

export default useTrackBoundingClient;
